import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { DatePicker } from '@mui/x-date-pickers';
import InputConsensus from 'components/InputConsensus';
import { formatDateToBrazilian, formatDateToISO8601 } from 'utils/date-time.utils';

const KeyboardDatePicker = props => {
  const {
    onlyMonthYear,
    disableToolbar,
    placeholder,
    margin,
    autoOk,
    label,
    format,
    value,
    onChange,
    minDate,
    minDateMessage,
    maxDate,
    maxDateMessage,
    name,
    shrink,
    fullWidth,
    ariaLabel,
    invalidDateMessage,
    invalidLabel,
    inputVariant,
    size,
    component: InputComponent,
    onError,
    views = onlyMonthYear ? ['year', 'month'] : ['year', 'month', 'day'],
    ...rest
  } = props;

  // Converte o valor para moment se for string
  const momentValue = useMemo(() => {
    if (!value) return null;
    if (moment.isMoment(value)) return value;
    if (typeof value === 'string') {
      const parsedDate = moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY-MM'], true);
      return parsedDate.isValid() ? parsedDate : null;
    }
    return moment(value);
  }, [value]);

  // Converte minDate para moment
  const momentMinDate = useMemo(() => {
    if (!minDate) return undefined;
    if (moment.isMoment(minDate)) return minDate;
    if (typeof minDate === 'string') {
      const parsed = moment(minDate, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
      return parsed.isValid() ? parsed : undefined;
    }
    return moment(minDate);
  }, [minDate]);

  // Converte maxDate para moment
  const momentMaxDate = useMemo(() => {
    if (!maxDate) return undefined;
    if (moment.isMoment(maxDate)) return maxDate;
    if (typeof maxDate === 'string') {
      const parsed = moment(maxDate, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
      return parsed.isValid() ? parsed : undefined;
    }
    return moment(maxDate);
  }, [maxDate]);

  const customReturnedValue = useCallback(
    value => {
      if (value) {
        return views.includes('day') ? formatDateToISO8601(value) : value;
      }

      return null;
    },
    [views],
  );

  const [validationError, setValidationError] = useState(null);

  const validationMessage = useMemo(() => {
    switch (validationError) {
      case 'minDate':
        return minDateMessage || `Data mínima permitida: ${formatDateToBrazilian(minDate)}`;
      case 'maxDate':
        return maxDateMessage || `Data máxima permitida: ${formatDateToBrazilian(maxDate)}`;
      case 'invalidDate':
        return invalidDateMessage;
      default:
        return invalidLabel;
    }
  }, [
    validationError,
    minDateMessage,
    maxDateMessage,
    invalidDateMessage,
    invalidLabel,
    minDate,
    maxDate,
  ]);

  return (
    <DatePicker
      {...rest}
      format={format}
      name={name}
      minDate={momentMinDate}
      maxDate={momentMaxDate}
      label={label}
      value={momentValue}
      onChange={value => onChange(customReturnedValue(value))}
      views={views}
      closeOnSelect={autoOk}
      enableAccessibleFieldDOMStructure={false}
      onError={(reason, value) => {
        setValidationError(reason);
        onError?.(reason, value);
      }}
      slots={{
        textField: InputComponent,
      }}
      slotProps={{
        textField: {
          fullWidth,
          margin,
          placeholder,
          variant: inputVariant,
          size,
          InputLabelProps: { shrink },
          name,
          error: Boolean(validationError),
          helperText: validationMessage,
        },
        openPickerButton: {
          'aria-label': ariaLabel,
        },
        toolbar: {
          hidden: disableToolbar,
        },
      }}
    />
  );
};

KeyboardDatePicker.propTypes = {
  autoOk: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['dialog', 'inline', 'static']),
  format: PropTypes.oneOf(['DD/MM/YYYY', 'MM/DD/YYYY', 'MM/YYYY']),
  label: PropTypes.string.isRequired,
  margin: PropTypes.oneOf(['normal', 'none', 'dense']),
  placeholder: PropTypes.string,
  name: PropTypes.string.isRequired,
  fullWidth: PropTypes.bool,
  minDate: PropTypes.any,
  minDateMessage: PropTypes.string,
  maxDate: PropTypes.any,
  maxDateMessage: PropTypes.string,
  ariaLabel: PropTypes.oneOf(['change date', 'change time']),
  invalidDateMessage: PropTypes.string,
  invalidLabel: PropTypes.string,
  component: PropTypes.any,
  disableToolbar: PropTypes.bool,
  onError: PropTypes.func,
  shrink: PropTypes.bool,
  inputVariant: PropTypes.oneOf(['filled', 'outlined', 'standard']),
  size: PropTypes.oneOf(['small', 'medium']),
  views: PropTypes.arrayOf(PropTypes.string),
  onlyMonthYear: PropTypes.bool,
};

KeyboardDatePicker.defaultProps = {
  autoOk: true,
  onChange: () => null,
  format: 'DD/MM/YYYY',
  margin: 'none',
  placeholder: 'dd/mm/aaaa',
  fullWidth: true,
  ariaLabel: 'change date',
  invalidDateMessage: 'Data inválida',
  invalidLabel: null,
  component: InputConsensus,
  disableToolbar: true,
  shrink: true,
  minDateMessage: 'A data não deve ser anterior à data mínima',
  inputVariant: 'standard',
  onlyMonthYear: false,
};

export default KeyboardDatePicker;
